import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import { Inbox, FileBadge, BadgeCheck, ExternalLink, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  salary_certificate: { label: 'Salary Certificate', route: '/salary-certificate', icon: FileBadge, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  experience_certificate: { label: 'Experience Certificate', route: '/experience-certificate', icon: BadgeCheck, color: 'text-sky-600', bg: 'bg-sky-50' },
};

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const DocumentRequestsCard = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/api/document-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(Array.isArray(r.data) ? r.data : []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (req, status) => {
    setBusy(req.id);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/document-requests/${req.id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: status === 'issued' ? 'Marked as issued' : 'Request declined',
        description: `${req.employee_name} — ${req.request_label}`,
      });
      setRequests(prev => prev.filter(x => x.id !== req.id));
    } catch {
      toast({ title: 'Error', description: 'Could not update request', variant: 'destructive' });
    } finally { setBusy(null); }
  };

  if (requests.length === 0) return null;

  return (
    <Card className="border-violet-200 shadow-sm" data-testid="document-requests-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Inbox className="h-5 w-5 text-violet-600" />
          Document Requests
          <Badge className="bg-violet-100 text-violet-700 ml-1" data-testid="doc-requests-count">{requests.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {requests.map(req => {
          const meta = TYPE_META[req.request_type] || {};
          const Icon = meta.icon || FileBadge;
          return (
            <div key={req.id} className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50" data-testid={`doc-request-${req.id}`}>
              <div className={`h-10 w-10 rounded-lg ${meta.bg || 'bg-gray-50'} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`h-5 w-5 ${meta.color || 'text-gray-500'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {req.employee_name} <span className="text-xs font-normal text-gray-400">· {req.employee_code}</span>
                </p>
                <p className="text-xs text-gray-500">
                  {meta.label || req.request_label} · {req.department || '—'} · Requested {fmtDate(req.created_at)}
                </p>
                {req.note ? <p className="text-[11px] text-gray-400 mt-0.5 italic">“{req.note}”</p> : null}
                {req.addressed_to ? <p className="text-[11px] text-violet-600 mt-0.5 font-medium">Addressed to: {req.addressed_to}</p> : null}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`${meta.route || '/salary-certificate'}?employee_id=${req.employee_id}&request_id=${req.id}`)}
                  data-testid={`issue-request-${req.id}`}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Issue
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={busy === req.id}
                  onClick={() => act(req, 'issued')}
                  data-testid={`mark-issued-${req.id}`}
                >
                  <Check className="h-3.5 w-3.5 mr-1" /> Done
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-rose-600 hover:bg-rose-50"
                  disabled={busy === req.id}
                  onClick={() => act(req, 'rejected')}
                  data-testid={`reject-request-${req.id}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default DocumentRequestsCard;
